import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowLeft, Ticket, CreditCard, Smartphone, BarChart, Shield, Users } from 'lucide-react';

export const InnovBillPage = () => {
  const features = [
    {
      icon: <Ticket className="w-6 h-6" />,
      title: 'Création de billets',
      description: 'Créez et personnalisez vos billets en quelques clics'
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: 'Paiement sécurisé',
      description: 'Acceptez les paiements en ligne en toute sécurité'
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: 'Billets numériques',
      description: 'Billets électroniques avec codes QR pour une validation rapide'
    },
    {
      icon: <BarChart className="w-6 h-6" />,
      title: 'Statistiques en temps réel',
      description: 'Suivez vos ventes et analysez vos performances'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Anti-fraude',
      description: 'Protection contre la duplication et la fraude'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Gestion des participants',
      description: 'Base de données complète de vos participants'
    }
  ];

  const eventTypes = [
    'Concerts et spectacles',
    'Conférences et séminaires',
    'Festivals et événements culturels',
    'Événements sportifs',
    'Galas et soirées bénéfices',
    'Formations et ateliers'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-blue-50">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-20">
        <Button
          onClick={() => window.location.href = '/'}
          variant="ghost"
          className="mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à l'accueil
        </Button>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-block p-6 bg-gradient-to-br from-pink-600 to-pink-800 rounded-full mb-6">
            <Ticket className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-pink-700 to-blue-600 bg-clip-text text-transparent">
            Innov-Bill
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
            La solution de billeterie en ligne complète pour vos événements
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-pink-600 to-pink-800 rounded-lg flex items-center justify-center mb-4 text-white">
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* How it Works */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-12">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">
            Comment ça fonctionne?
          </h2>
          <div className="space-y-8">
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 bg-pink-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-xl">
                1
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Créez votre événement</h3>
                <p className="text-gray-600">Configurez les détails de votre événement, les types de billets et les prix</p>
              </div>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 bg-pink-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-xl">
                2
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Diffusez et vendez</h3>
                <p className="text-gray-600">Partagez le lien de vente et commencez à vendre vos billets en ligne</p>
              </div>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 bg-pink-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-xl">
                3
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Gérez et contrôlez</h3>
                <p className="text-gray-600">Validez les billets le jour J avec notre système de scan QR</p>
              </div>
            </div>
          </div>
        </div>

        {/* Event Types */}
        <div className="bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
            Parfait pour tous types d'événements
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {eventTypes.map((type, index) => (
              <div key={index} className="flex items-center gap-3 bg-white rounded-lg p-4 shadow">
                <div className="w-2 h-2 bg-pink-600 rounded-full"></div>
                <span className="text-gray-700">{type}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-pink-600 to-blue-600 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Prêt à vendre vos billets en ligne?
          </h3>
          <p className="mb-6 text-lg">
            Lancez votre billetterie en quelques minutes avec Innov-Bill
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => window.location.href = '/#contact'}
              className="bg-white text-pink-700 hover:bg-pink-50 px-8 py-6 text-lg"
            >
              Démarrer gratuitement
            </Button>
            <Button
              onClick={() => window.location.href = '/#contact'}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
            >
              Demander une démo
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
